import React from 'react';
import { BarChart3, Users, Link, TrendingUp } from 'lucide-react';

const AnalyticsMetrics = ({ metrics, trackingLinks, clickData }) => {
  // Calculate additional metrics from the data
  const calculateConversionRate = () => {
    if (!metrics?.totalClicks || metrics?.totalClicks === 0) return 0;
    return ((metrics?.uniqueVisitors || 0) / metrics?.totalClicks * 100)?.toFixed(1);
  };

  const calculateTopPerformingSource = () => {
    if (!clickData || clickData?.length === 0) return 'N/A';
    
    const sourceCount = clickData?.reduce((acc, click) => {
      acc[click.source] = (acc?.[click?.source] || 0) + 1;
      return acc;
    }, {});
    
    const topSource = Object.entries(sourceCount)?.reduce((max, [source, count]) => {
      return count > max?.count ? { source, count } : max;
    }, { source: 'N/A', count: 0 });
    
    return topSource?.source;
  };

  const metricsData = [
    {
      name: 'Total des Clics',
      value: metrics?.totalClicks || 0,
      icon: BarChart3,
      change: '+12%',
      changeType: 'positive',
      description: 'Nombre total de clics sur vos liens'
    },
    {
      name: 'Visiteurs Uniques',
      value: metrics?.uniqueVisitors || 0,
      icon: Users,
      change: '+8%',
      changeType: 'positive',
      description: 'Nombre de visiteurs distincts'
    },
    {
      name: 'Liens Actifs',
      value: metrics?.activeLinks || 0,
      icon: Link,
      change: '+2',
      changeType: 'neutral',
      description: 'Liens de suivi actuellement actifs'
    },
    {
      name: 'Taux d\'Engagement',
      value: calculateConversionRate() + '%',
      icon: TrendingUp,
      change: '+5.2%',
      changeType: 'positive',
      description: 'Ratio visiteurs uniques / clics totaux'
    }
  ];

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000)?.toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000)?.toFixed(1) + 'K';
    }
    return num?.toString();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metricsData?.map((metric) => {
        const IconComponent = metric?.icon;
        return (
          <div
            key={metric?.name}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">
                  {metric?.name}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {typeof metric?.value === 'number' && metric?.value > 999 
                    ? formatNumber(metric?.value) 
                    : metric?.value}
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <IconComponent className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className={`inline-flex items-center text-sm font-medium ${
                metric?.changeType === 'positive' ?'text-green-600' 
                  : metric?.changeType === 'negative' ?'text-red-600' :'text-gray-600'
              }`}>
                {metric?.changeType === 'positive' && (
                  <TrendingUp className="h-4 w-4 mr-1" />
                )}
                {metric?.change}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {metric?.description}
            </p>
          </div>
        );
      })}
      {/* Additional Context Cards */}
      <div className="md:col-span-2 lg:col-span-4 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Source Principale
                </p>
                <p className="text-lg font-bold text-blue-700 mt-1">
                  {calculateTopPerformingSource()}
                </p>
              </div>
              <div className="text-2xl">📱</div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900">
                  Pays Principal
                </p>
                <p className="text-lg font-bold text-green-700 mt-1">
                  {clickData?.find(click => click?.country)?.country || 'France'}
                </p>
              </div>
              <div className="text-2xl">🇫🇷</div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-900">
                  Appareil Principal
                </p>
                <p className="text-lg font-bold text-purple-700 mt-1">
                  {clickData?.find(click => click?.device_type)?.device_type || 'Mobile'}
                </p>
              </div>
              <div className="text-2xl">📱</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsMetrics;